import {addMilliseconds, differenceInMilliseconds, format} from 'date-fns';
import {millisecondsInDay} from 'date-fns/constants';

/** `GET /billing/credits` — display credits (already ÷100 server-side). */
export type CreditsBalance = {
  allowed: boolean;
  balanceCredits: number;
  /** How far past zero the org has spent; `balanceCredits` is clamped at 0 server-side. */
  exceededCredits: number;
  validRemainingCredits: number;
  /** Σ granted on the currently valid grants — the meter's denominator. */
  validGrantedCredits: number;
  debtCredits: number;
};

/**
 * useState key surfaces bump (increment) after changing the balance — e.g. the
 * Credit Usage purchase drawer — so the sidebar meter re-reads without a page
 * refresh.
 */
export const CREDITS_REFRESH_STATE_KEY = 'credits-refresh-tick';

/** The metering window, from `getCurrentBillingMonthRange`. `end` is exclusive. */
export type BillingMonth = {start: Date; end: Date};

export type BillingPeriodProgress = {
  daysLeft: number;
  /** Share of the period still remaining, 0–100. Positions the meter's tick. */
  daysLeftPercent: number;
};

const clampPercent = (value: number): number => Math.min(100, Math.max(0, Math.round(value)));

/** Balance can dip below zero (soft cap); "credits left" never reads negative. */
export const creditsLeft = (balanceCredits: number): number =>
  Math.max(0, Math.floor(balanceCredits));

/** Overspend reads as a negative balance, since "0 left" hides how deep the hole is. */
export const formatCredits = (balanceCredits: number, exceededCredits = 0): string =>
  exceededCredits > 0
    ? `-${Math.round(exceededCredits).toLocaleString('en-US')}`
    : creditsLeft(balanceCredits).toLocaleString('en-US');

/**
 * Credits consumed out of the period's grants — the unfilled part of the bar.
 * Null when nothing was granted, since there's no total to subtract from.
 */
export const creditsUsed = (balanceCredits: number, allowanceCredits: number): number | null => {
  if (!Number.isFinite(allowanceCredits) || allowanceCredits <= 0) return null;
  return Math.max(0, Math.round(allowanceCredits - creditsLeft(balanceCredits)));
};

/**
 * Share of the period's granted credits still unspent, 0–100. Null when
 * nothing was granted — a percentage of zero has no meaning to show.
 */
export const creditsLeftPercent = (
  balanceCredits: number,
  allowanceCredits: number
): number | null => {
  if (!Number.isFinite(allowanceCredits) || allowanceCredits <= 0) return null;
  return clampPercent((creditsLeft(balanceCredits) / allowanceCredits) * 100);
};

/**
 * Days remaining in the Stripe billing period. Takes unix seconds (as they
 * arrive on `org.subscription`); null when either bound is missing or the
 * period is degenerate, which hides the days half of the meter.
 *
 * Elapsed-time math, not calendar days: the period is a UTC instant range, so
 * counting calendar days would shift the reading with the viewer's timezone.
 * Floored, so the count is whole days still available — the final partial day
 * reads as 0.
 */
export const billingPeriodProgress = (
  period: BillingMonth | null,
  now: Date = new Date()
): BillingPeriodProgress | null => {
  if (!period) return null;

  const {start, end} = period;
  const totalDays = differenceInMilliseconds(end, start) / millisecondsInDay;
  if (totalDays <= 0) return null;

  const daysLeft = Math.max(0, Math.floor(differenceInMilliseconds(end, now) / millisecondsInDay));
  return {daysLeft, daysLeftPercent: clampPercent((daysLeft / totalDays) * 100)};
};

export const daysLeftLabel = (daysLeft: number): string =>
  `${daysLeft === 1 ? 'Day' : 'Days'} left in cycle`;

/** "(N day)" / "(N days)" -- the parenthetical next to the cycle reset timestamp. */
export const daysLeftParenLabel = (daysLeft: number): string =>
  `(${daysLeft} ${daysLeft === 1 ? 'day' : 'days'})`;

// UTC on purpose (matches creditUsage.helpers.ts / platformBilling.helpers.ts):
// `date` is a Stripe UTC instant, so local-tz formatting would shift the shown
// date for viewers on either side of a UTC day boundary.
export const formatCycleResetDate = (date: Date): string =>
  new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(date);

/**
 * How the credit burn compares to the cycle's pace. Spending in step with the
 * clock is `onPace`; falling behind by more than a few points means the credits
 * run out before the cycle renews.
 */
export type PaceState = 'onPace' | 'overPace' | 'critical' | 'outOfCredits';

/** Points of credits-behind-days before the meter warns / escalates to red. */
const OVER_PACE_THRESHOLD = 6;
const CRITICAL_THRESHOLD = 15;

/**
 * Pace is the gap between the two percentages the meter already shows: credits
 * left vs days left. Null percentages (no allowance, no billing period) leave
 * nothing to compare, so the meter stays quiet.
 */
export const paceState = (
  creditsPercent: number | null,
  daysLeftPercent: number | null,
  balanceCredits: number
): PaceState => {
  if (creditsLeft(balanceCredits) <= 0) return 'outOfCredits';
  if (creditsPercent === null || daysLeftPercent === null) return 'onPace';

  const behindBy = daysLeftPercent - creditsPercent;
  if (behindBy >= CRITICAL_THRESHOLD) return 'critical';
  if (behindBy > OVER_PACE_THRESHOLD) return 'overPace';
  return 'onPace';
};

/**
 * When the balance hits zero at the current burn rate, from spend-so-far over
 * time-so-far. Null when nothing has been spent yet, the cycle just started, or
 * the projection lands past the renewal — in those cases there's no run-out
 * date worth naming.
 */
export const projectedRunOutDate = (
  balanceCredits: number,
  allowanceCredits: number,
  period: BillingMonth | null,
  now: Date = new Date()
): Date | null => {
  if (!period || !Number.isFinite(allowanceCredits)) return null;

  const remaining = creditsLeft(balanceCredits);
  const spent = allowanceCredits - remaining;
  const elapsedMs = differenceInMilliseconds(now, period.start);
  if (spent <= 0 || elapsedMs <= 0 || remaining <= 0) return null;

  const runOut = addMilliseconds(now, (remaining / spent) * elapsedMs);
  return runOut >= period.end ? null : runOut;
};

export const formatRunOutDate = (date: Date): string => format(date, 'MMM d');
